"use client";

import Link from "next/link";
import { CircleArrowRight } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { CtaButton } from "@/components/layout/CtaButton";
import { submitForm } from "@/lib/submitForm";

export type ContactInquiryFormData = {
  fullName: string;
  email: string;
  phone: string;
  message: string;
};

export type ContactInquiryFormProps = {
  id?: string;
  submitLabel?: string;
  onSubmit?: (data: ContactInquiryFormData) => void | Promise<void>;
  className?: string;
};

export function ContactInquiryForm({
  id = "contact-form",
  submitLabel = "Submit Your Inquiry",
  onSubmit,
  className,
}: ContactInquiryFormProps) {
  const router = useRouter();
  const [formData, setFormData] = useState<ContactInquiryFormData>({
    fullName: "",
    email: "",
    phone: "",
    message: "",
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");

  const fieldId = (name: string) => `${id}-${name}`;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus("idle");

    try {
      const result = await submitForm({
        source: id,
        fields: formData,
        honeypot,
      });

      if (!result.ok) {
        setStatus("error");
        setStatusMessage(result.error);
        return;
      }

      if (onSubmit) {
        await onSubmit(formData);
      }

      const ppcData =
        typeof window !== "undefined"
          ? window.sessionStorage.getItem("ubp:ppc")
          : null;

      if (
        typeof window !== "undefined" &&
        Array.isArray(
          (window as unknown as { dataLayer?: unknown[] }).dataLayer,
        )
      ) {
        (window as unknown as { dataLayer: unknown[] }).dataLayer.push({
          event: "contact_form_submit",
          ...formData,
          ppc: ppcData ? JSON.parse(ppcData) : null,
        });
      }

      setFormData({ fullName: "", email: "", phone: "", message: "" });
      setPrivacyAccepted(false);
      router.push("/thank-you/");
    } catch (error) {
      console.error("Contact form submission error:", error);
      setStatus("error");
      setStatusMessage("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldClass =
    "contact-field peer w-full rounded-[4px] px-3 pb-2 pt-6 placeholder:text-transparent";

  const labelClass =
    "contact-field-label pointer-events-none absolute left-3 top-2 text-xs transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-base peer-focus:top-2 peer-focus:translate-y-0 peer-focus:text-xs";

  return (
    <form
      id={id}
      onSubmit={handleSubmit}
      className={["contact-form", className].filter(Boolean).join(" ")}
    >
      <div className="space-y-0">
        {/* Honeypot — hidden from real users, bots that autofill every field will trip it */}
        <input
          type="text"
          name="website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden"
        />

        <div className="form-floating-group relative">
          <input
            type="text"
            id={fieldId("fullName")}
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            placeholder=" "
            required
            className={fieldClass}
          />
          <label htmlFor={fieldId("fullName")} className={labelClass}>
            Full name
          </label>
        </div>

        <div className="grid gap-0 sm:grid-cols-2">
          <div className="form-floating-group relative">
            <input
              type="email"
              id={fieldId("email")}
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder=" "
              required
              className={fieldClass}
            />
            <label htmlFor={fieldId("email")} className={labelClass}>
              Email address
            </label>
          </div>
          <div className="form-floating-group relative">
            <input
              type="tel"
              id={fieldId("phone")}
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder=" "
              required
              className={fieldClass}
            />
            <label htmlFor={fieldId("phone")} className={labelClass}>
              (999) 999 9999
            </label>
          </div>
        </div>

        <div className="form-floating-group relative">
          <textarea
            id={fieldId("message")}
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder=" "
            rows={8}
            className={`${fieldClass} min-h-[200px] resize-y`}
          />
          <label htmlFor={fieldId("message")} className={labelClass}>
            Message
          </label>
        </div>
      </div>

      <div className="my-4 flex items-start gap-2">
        <input
          id={fieldId("privacy")}
          type="checkbox"
          required
          checked={privacyAccepted}
          onChange={(e) => setPrivacyAccepted(e.target.checked)}
          className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-[#b46c29]"
        />
        <label
          htmlFor={fieldId("privacy")}
          className="contact-privacy-label cursor-pointer"
        >
          I agree to the{" "}
          <Link
            href="/terms-condition"
            className="text-[#b46c29] underline-offset-2 hover:underline"
          >
            Terms &amp; Conditions
          </Link>{" "}
          Of United Book Publishing
        </label>
      </div>

      <CtaButton
        type="submit"
        variant="primary"
        size="md"
        icon={<CircleArrowRight className="h-5 w-5" aria-hidden="true" />}
        iconPosition="right"
        className="s1-form-submit w-full disabled:cursor-not-allowed disabled:opacity-50"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Submitting..." : submitLabel}
      </CtaButton>

      {status !== "idle" && (
        <p
          role="status"
          className={
            status === "success"
              ? "mt-3 text-sm text-[#29361a]"
              : "mt-3 text-sm text-red-600"
          }
        >
          {statusMessage}
        </p>
      )}
    </form>
  );
}
